// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Focusable from 'components/focus/Focusable';
import focusManager from 'support/focus/FocusManager';
import appActions from 'common/support/applicationActions';

import styles from './FocusGroup.module.scss';

class FocusGroup extends React.PureComponent {
  divRef = React.createRef();

  focusId = null;

  focusIndex = 0;

  constructor(props) {
    super(props);

    this.focusIndex = props.initialFocusIndex || 0;
    // Need to set focusId here since there's the need to allow
    // passing the id in as a prop.
    this.focusId = props.focusId || focusManager.getNewUniqueId();
  }

  componentDidMount() {
    focusManager.addFocusChangedListener(this.onFocusChanged);
    this.resetFocusIndex();
  }

  componentDidUpdate(prevProps) {
    const { children } = this.props;
    const { children: oldChildren } = prevProps;

    if (children !== oldChildren) {
      this.resetFocusIndex();
    }
  }

  componentWillUnmount() {
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onAction = action => {
    const { onActionOverride, onFocusIndexOverride, horizontal } = this.props;
    const { focusIndex } = this;

    if (onActionOverride(action)) {
      return true;
    }

    const newFocusIndex = onFocusIndexOverride(action, focusIndex);

    if (newFocusIndex === 'handled') {
      return true;
    }

    if (newFocusIndex === focusIndex) {
      return false;
    }

    if (newFocusIndex || newFocusIndex === 0) {
      return this.updateFocusIndex(newFocusIndex);
    }

    switch (action) {
      case appActions.moveLeft:
        return horizontal && this.moveToPrevFocus();
      case appActions.moveRight:
        return horizontal && this.moveToNextFocus();
      case appActions.moveUp:
        return !horizontal && this.moveToPrevFocus();
      case appActions.moveDown:
        return !horizontal && this.moveToNextFocus();
      default:
        return false;
    }
  };

  onFocusSet = value => {
    const {
      onFocusSetOverride,
      onFocusDelegated,
      onFocusIndexChanged,
    } = this.props;
    const { focusIndex } = this;
    let newFocusIndex = focusIndex;

    if (!value) return;

    const indexOverride = onFocusSetOverride(value);
    // null >= 0 returns true so need to handle separately
    if (indexOverride >= 0 && indexOverride !== null) {
      newFocusIndex = indexOverride;
    }

    const focusables = focusManager.getFocusableChildren(
      this.focusId,
      this.divRef.current,
    );

    if (newFocusIndex > focusables.length - 1) {
      newFocusIndex = focusables.length - 1;
      this.focusIndex = newFocusIndex;
      onFocusIndexChanged(newFocusIndex, focusIndex);
    }

    // Delegate the focus to the appropriate focusable child
    if (focusables[newFocusIndex]) {
      focusManager.setCurrentFocus(
        focusables[newFocusIndex].getAttribute('data-fcsid'),
      );
      onFocusDelegated(newFocusIndex);
    }
  };

  onFocusChanged = () => {
    this.resetFocusIndex();
  };

  // This method "calibrates" the focus index in order to
  // stay in sync with unexpected/unhandled changes to the
  // focused item in the FocusManager.  (ie. a Focusable
  // component initializing focus, or a tv remote clicking
  // on an item)
  resetFocusIndex() {
    const { onFocusIndexChanged } = this.props;
    const { focusIndex } = this;
    if (!focusManager.isFocusAncestor(this.focusId, this.divRef.current))
      return;

    const newFocusIndex = this.deriveFocusIndex(focusManager.currentFocusId);

    if (newFocusIndex >= 0) {
      this.focusIndex = newFocusIndex;
      onFocusIndexChanged(newFocusIndex, focusIndex);
    }
  }

  deriveFocusIndex(focusId, focusDescendants) {
    const focusables =
      focusDescendants ||
      focusManager.getFocusableChildren(this.focusId, this.divRef.current);

    const childIndex = focusables.findIndex(
      item =>
        item.getAttribute('data-fcsid') === (focusId && focusId.toString()),
    );

    if (childIndex >= 0) {
      return childIndex;
    }

    // Check owners
    const focusedEl = focusManager.getFocusableElement(
      focusId,
      this.divRef.current,
    );
    const focusOwnerId = focusManager.getFocusOwnerId(focusedEl);
    if (!focusOwnerId) return undefined;
    return this.deriveFocusIndex(focusOwnerId, focusables);
  }

  moveToNextFocus() {
    const { focusIndex } = this;
    return this.updateFocusIndex(focusIndex + 1);
  }

  moveToPrevFocus() {
    const { focusIndex } = this;
    return this.updateFocusIndex(focusIndex - 1);
  }

  updateFocusIndex(nextFocusIndex) {
    const {
      wrapEnd,
      wrapStart,
      onFocusIndexChanged,
      onEndOfNavigation,
    } = this.props;
    const { focusIndex } = this;

    let attemptFocusAt = nextFocusIndex;
    const focusables = focusManager.getFocusableChildren(
      this.focusId,
      this.divRef.current,
    );

    let nextFocusEl = focusables[attemptFocusAt];

    if (!nextFocusEl) {
      if (wrapEnd && attemptFocusAt > focusIndex) {
        attemptFocusAt = 0;
      } else if (wrapStart && attemptFocusAt < focusIndex) {
        attemptFocusAt = focusables.length - 1;
      }

      nextFocusEl = focusables[attemptFocusAt];
    }

    if (!nextFocusEl || attemptFocusAt === focusIndex) {
      onEndOfNavigation();
      return false;
    }

    this.focusIndex = attemptFocusAt;
    onFocusIndexChanged(attemptFocusAt, focusIndex);
    focusManager.setCurrentFocus(nextFocusEl.getAttribute('data-fcsid'));
    return true;
  }

  render() {
    const {
      focusId,
      className,
      horizontal,
      initialFocusIndex,
      children,
      onActionOverride,
      onFocusSetOverride,
      onFocusIndexOverride,
      onFocusIndexChanged,
      onFocusDelegated,
      wrapStart,
      wrapEnd,
      ...rest
    } = this.props;

    return (
      <Focusable
        ref={this.divRef}
        focusId={this.focusId && this.focusId.toString()}
        className={classNames(className, { [styles.horizontal]: horizontal })}
        onFocusSet={this.onFocusSet}
        onAction={this.onAction}
        render={() => children}
        disableFocusOnHover
        {...rest}
      />
    );
  }
}

FocusGroup.propTypes = {
  className: PropTypes.string,
  horizontal: PropTypes.bool,
  wrapStart: PropTypes.bool,
  wrapEnd: PropTypes.bool,
  initialFocusIndex: PropTypes.number,
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFocusIndexChanged: PropTypes.func,
  onActionOverride: PropTypes.func,
  onFocusSetOverride: PropTypes.func,
  onFocusIndexOverride: PropTypes.func,
  onFocusDelegated: PropTypes.func,
  children: PropTypes.node,
  onEndOfNavigation: PropTypes.func,
};

FocusGroup.defaultProps = {
  className: null,
  horizontal: false,
  wrapStart: false,
  wrapEnd: false,
  initialFocusIndex: 0,
  focusId: null,
  onFocusIndexChanged: () => {},
  onActionOverride: () => false,
  onFocusSetOverride: () => {},
  onFocusIndexOverride: () => false,
  onFocusDelegated: () => {},
  children: null,
  onEndOfNavigation: () => {},
};

export default FocusGroup;
